import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '../modules/service/auth.service';

// ── View modes ─────────────────────────────────────────────────────────────────
// 'request'        – no token in URL; show the email input form
// 'verifying'      – token found, calling verifyResetToken(), show spinner
// 'invalid-token'  – verifyResetToken() returned non-000 or error
// 'new-password'   – token valid; show strength-meter + new-password form
// 'success'        – updatePassword() returned 000; show success screen
type ViewMode = 'request' | 'verifying' | 'invalid-token' | 'new-password' | 'success';

@Component({
  selector: 'app-holl-reset-password',
  templateUrl: './holl-reset-password.component.html',
  styleUrls: ['./holl-reset-password.component.scss']
})
export class HollResetPasswordComponent implements OnInit {

  // ── Request-email mode ──────────────────────────────────────────────────
  email        = '';
  isLoading    = false;
  errorMessage = '';
  successMessage = '';
  emailSent    = false;

  // ── Token mode ──────────────────────────────────────────────────────────
  viewMode: ViewMode = 'request';
  resetToken   = '';

  newPassword     = '';
  confirmPassword = '';
  showNew         = false;
  showConfirm     = false;

  isSubmitting = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private auth: AuthService
  ) {}

  ngOnInit() {
    var token = this.route.snapshot.queryParamMap.get('token') || '';
    if (token) {
      this.resetToken = token;
      this.viewMode   = 'verifying';
      this.verifyToken();
    }
  }

  // ── Token verification ───────────────────────────────────────────────────

  private verifyToken() {
    this.auth.verifyResetToken(this.resetToken).subscribe(
      (res) => {
        if (res.responseCode === '000') {
          this.viewMode = 'new-password';
        } else {
          this.viewMode    = 'invalid-token';
          this.errorMessage = res.responseMessage || 'This reset link is invalid or has expired.';
        }
      },
      (err) => {
        this.viewMode     = 'invalid-token';
        this.errorMessage = err && err.error && err.error.responseMessage
          ? err.error.responseMessage
          : 'This reset link is invalid or has expired.';
      }
    );
  }

  // ── Password strength helpers ────────────────────────────────────────────

  get rules() {
    var p = this.newPassword;
    return {
      length:    p.length >= 8,
      uppercase: /[A-Z]/.test(p),
      lowercase: /[a-z]/.test(p),
      number:    /[0-9]/.test(p),
      special:   /[^A-Za-z0-9]/.test(p)
    };
  }

  get strengthScore(): number {
    var r = this.rules;
    return [r.length, r.uppercase, r.lowercase, r.number, r.special].filter(Boolean).length;
  }

  get strengthLabel(): string {
    var s = this.strengthScore;
    if (s <= 1) { return 'Very weak'; }
    if (s === 2) { return 'Weak'; }
    if (s === 3) { return 'Fair'; }
    if (s === 4) { return 'Good'; }
    return 'Strong';
  }

  get strengthClass(): string {
    var s = this.strengthScore;
    if (s <= 1) { return 'very-weak'; }
    if (s === 2) { return 'weak'; }
    if (s === 3) { return 'fair'; }
    if (s === 4) { return 'good'; }
    return 'strong';
  }

  get passwordsMatch(): boolean {
    return this.newPassword.length > 0 && this.newPassword === this.confirmPassword;
  }

  get newPasswordFormValid(): boolean {
    return this.strengthScore >= 3 && this.passwordsMatch;
  }

  // ── Submit new password ──────────────────────────────────────────────────

  submitNewPassword(event: Event) {
    event.preventDefault();
    this.errorMessage   = '';
    this.successMessage = '';

    if (this.strengthScore < 3) {
      this.errorMessage = 'Your password is too weak. Please follow the requirements below.';
      return;
    }

    if (!this.passwordsMatch) {
      this.errorMessage = 'Passwords do not match. Please check and try again.';
      return;
    }

    this.isSubmitting = true;

    this.auth.updatePassword(this.resetToken, this.newPassword).subscribe(
      (res) => {
        this.isSubmitting = false;
        if (res.responseCode === '000') {
          this.viewMode = 'success';
        } else {
          this.errorMessage = res.responseMessage || 'Failed to update password. Please try again.';
        }
      },
      (err) => {
        this.isSubmitting = false;
        this.errorMessage = err && err.error && err.error.responseMessage
          ? err.error.responseMessage
          : 'Failed to update password. Please check your connection and try again.';
      }
    );
  }

  // ── Request-email flow ───────────────────────────────────────────────────

  goBack() {
    this.router.navigate(['']);
  }

  submitReset(event: Event) {
    event.preventDefault();
    this.errorMessage  = '';
    this.successMessage = '';

    var trimmed = this.email.trim();

    if (!trimmed) {
      this.errorMessage = 'Please enter your email address.';
      return;
    }

    var emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailPattern.test(trimmed)) {
      this.errorMessage = 'Please enter a valid email address.';
      return;
    }

    this.isLoading = true;

    this.auth.resetPassword(trimmed).subscribe(
      (res) => {
        this.isLoading = false;
        if (res.responseCode === '000') {
          this.emailSent      = true;
          this.successMessage = res.responseMessage ||
            'Password reset instructions have been sent to ' + this.maskEmail(trimmed) + '.';
        } else {
          this.errorMessage = res.responseMessage || 'Failed to send reset link. Please try again.';
        }
      },
      (err) => {
        this.isLoading = false;
        this.errorMessage = err && err.error && err.error.responseMessage
          ? err.error.responseMessage
          : 'Failed to send reset link. Please check your connection and try again.';
      }
    );
  }

  resendEmail() {
    this.errorMessage   = '';
    this.successMessage = '';
    this.isLoading      = true;

    this.auth.resetPassword(this.email.trim()).subscribe(
      (res) => {
        this.isLoading = false;
        if (res.responseCode === '000') {
          this.successMessage = res.responseMessage ||
            'Instructions resent to ' + this.maskEmail(this.email.trim()) + '.';
        } else {
          this.errorMessage = res.responseMessage || 'Failed to resend. Please try again.';
        }
      },
      (err) => {
        this.isLoading = false;
        this.errorMessage = err && err.error && err.error.responseMessage
          ? err.error.responseMessage
          : 'Failed to resend. Please check your connection and try again.';
      }
    );
  }

  private maskEmail(email: string): string {
    var parts = email.split('@');
    var local  = parts[0] || '';
    var domain = parts[1] || '';
    if (local.length <= 2) {
      return local.charAt(0) + '***@' + domain;
    }
    return local.charAt(0) + '***' + local.charAt(local.length - 1) + '@' + domain;
  }
}
